/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */
import React, { useState, useCallback } from "react";
import { List, ListItem, ListSubheader, Button, Checkbox, FormControlLabel, TextField } from "@material-ui/core";
import FormTextField from "../../../components/FormTextField/FormTextField";

const getNameErrorMessage = (value) => {
	if (!value) {
		return "Sub-disposition name is required.";
	}
	if (value.length > 50) {
		return "Sub-disposition name cannot exceed 50 characters.";
	}
};

export const SubDispositionList = ({
	subDispositions,
	addSubDispositionHandler,
	removeSubDispositionHandler,
	updateSubDispositionsHandler,
	markSubDispositionAsDefaultHandler,
	formEdited,
	setFormEdited,
}) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [focusSubDisposition, setFocusSubDisposition] = useState(false);

	const handleSearchChange = useCallback((e) => {
		setSearchTerm(e.target.value);
	}, []);

	return (
		<div>
			<List
				component="nav"
				subheader={
					<ListSubheader disableSticky component="div" className="p-0">
						<div style={{ marginBottom: 8 }}>
							<TextField
								style={{ width: "100%" }}
								size="small"
								label="Search sub-dispositions"
								value={searchTerm}
								onChange={handleSearchChange}
								inputProps={{
									"data-test-id": "subdisposition-search-input",
								}}
							/>
						</div>
						<div
							style={{
								display: "flex",
								flexDirection: "row",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<span>Sub-dispositions</span>

							<Button
								variant="outlined"
								size="small"
								onClick={() => {
									addSubDispositionHandler();
									setFocusSubDisposition(true);
								}}
								data-test-id="subdisposition-add-button"
							>
								Add sub-disposition
							</Button>
						</div>
					</ListSubheader>
				}
			>
				{subDispositions?.length ? (
					subDispositions.map((x, index) => (
						<SubDispositionItem
							key={x.subDispositionId}
							formEdited={formEdited}
							setFormEdited={setFormEdited}
							subDisposition={x}
							searchTerm={searchTerm}
							removeSubDispositionHandler={removeSubDispositionHandler}
							markSubDispositionAsDefaultHandler={markSubDispositionAsDefaultHandler}
							setSubDisposition={(updatedSubDisposition) => {
								const copy = subDispositions.slice();
								copy[index] = updatedSubDisposition;
								updateSubDispositionsHandler(copy);
							}}
							focusSubDisposition={focusSubDisposition}
						/>
					))
				) : (
					<ListItem>
						<span className="text-danger font-italic">At least one sub-disposition is required.</span>
					</ListItem>
				)}
			</List>
		</div>
	);
};

const SubDispositionItem = ({
	subDisposition,
	removeSubDispositionHandler,
	markSubDispositionAsDefaultHandler,
	setSubDisposition,
	searchTerm,
	focusSubDisposition,
	formEdited,
	setFormEdited,
}) => {
	const updatePropertyHandler = React.useCallback(
		(property, value) => setSubDisposition({ ...subDisposition, [property]: value }),
		[subDisposition, setSubDisposition],
	);

	const searchMatch = subDisposition.subDispositionName.toLowerCase().includes(searchTerm.toLowerCase());

	return (
		<ListItem style={{ display: searchMatch ? "flex" : "none", padding: 0 }}>
			<div className="d-flex align-items-center justify-content-between w-100 flex-wrap mb-3 mb-lg-0">
				<FormTextField
					autoFocus={focusSubDisposition}
					formEdited={formEdited}
					setEdited={setFormEdited}
					required
					errorMessage={getNameErrorMessage(subDisposition.subDispositionName)}
					startingValue={subDisposition.subDispositionName}
					setConfigValue={updatePropertyHandler}
					valueName={"subDispositionName"}
					styleObject={{ minWidth: 120, flex: 1 }}
					enableDebounce
					errorCheck={(val) => !val || val.length > 50}
					inputProps={{
						"data-test-id": "subdisposition-name-input",
					}}
				/>
				<FormControlLabel
					value="top"
					style={{ paddingTop: 12, marginLeft: 20 }}
					control={
						<Checkbox
							disabled={subDisposition.isDefault}
							size="small"
							color="secondary"
							style={{ opacity: !subDisposition.isDefault ? 1 : 0.4 }}
							onChange={(event) => {
								updatePropertyHandler("isEnabled", event.target.checked);
								setFormEdited(true);
							}}
							checked={subDisposition.isEnabled}
							inputProps={{
								"data-test-id": "subdisposition-enable-checkbox",
							}}
						/>
					}
					label={<span style={{ color: "rgba(0, 0, 0, 0.54)", fontSize: 14 }}>Enable</span>}
				/>
				<div>
					<Button
						variant="outlined"
						size="small"
						disabled={subDisposition.isDefault}
						style={{ opacity: !subDisposition.isDefault ? 1 : 0.4, color: "#cc1f20", borderColor: "#cc1f20" }}
						onClick={() => removeSubDispositionHandler(subDisposition.subDispositionId)}
						data-test-id="subdisposition-remove-btn"
					>
						Remove
					</Button>
					<Button
						variant="outlined"
						size="small"
						disabled={subDisposition.isDefault}
						style={{ marginLeft: 10 }}
						onClick={() => markSubDispositionAsDefaultHandler(subDisposition.subDispositionId)}
						data-test-id="subdisposition-default-btn"
					>
						Default
					</Button>
				</div>
			</div>
		</ListItem>
	);
};
